import { useState, useEffect } from 'react'
import { Building2, Check, X, Loader2 } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { getActivePlans, createCheckoutSession, calculateAnnualSavings, type Plan, type BillingInterval } from '../services/billing'
import { formatPenceCompact } from '../utils/currency'

interface UpgradeModalProps {
  action?: string
  onClose: () => void
}

/**
 * Modal shown when a read-only user attempts a write action or clicks "Choose a Plan".
 * Displays plan selection with monthly/annual toggle and redirects to Stripe Checkout.
 */
export default function UpgradeModal({ action, onClose }: UpgradeModalProps) {
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly')
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState('')
  const { subscriptionStatus } = useAuth()

  useEffect(() => {
    getActivePlans()
      .then(data => setPlans(data))
      .catch(err => {
        setError('Failed to load plans: ' + (err instanceof Error ? err.message : 'Unknown error'))
      })
      .finally(() => setLoading(false))
  }, [])

  const handleSubscribe = async (planId: string, trialDays?: number) => {
    setError('')
    setActionLoading(true)

    try {
      const { url } = await createCheckoutSession({
        planId,
        interval: billingInterval,
        trialDays,
        successUrl: `${window.location.origin}/billing/success`,
        cancelUrl: window.location.href,
      })

      window.location.href = url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
      setActionLoading(false)
    }
  }

  const formatPrice = formatPenceCompact

  return (
    <div
      className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className="rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
        data-readonly-safe="true"
      >
        <div className="p-5 md:p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="text-center flex-1">
              <Building2 className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-accent)' }} />
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Choose a plan
              </h2>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {action
                  ? `Subscribe to ${action}`
                  : 'Subscribe to unlock full access to your dashboard'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 transition flex-shrink-0"
              style={{ color: 'var(--text-muted)' }}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: 'var(--color-accent)' }} />
              <p style={{ color: 'var(--text-muted)' }}>Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Billing interval toggle */}
              <div className="flex justify-center mb-4" data-readonly-safe="true">
                <div className="inline-flex rounded-xl bg-[var(--bg-surface)] p-1 border border-[var(--border-color)]">
                  <button
                    onClick={() => setBillingInterval('monthly')}
                    data-readonly-safe="true"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition cursor-pointer ${
                      billingInterval === 'monthly'
                        ? 'bg-[var(--color-accent)] text-white'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingInterval('annual')}
                    data-readonly-safe="true"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition cursor-pointer ${
                      billingInterval === 'annual'
                        ? 'bg-[var(--color-accent)] text-white'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    Annual
                    <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full" style={{ backgroundColor: 'white', color: '#16a34a' }}>Save 20%</span>
                  </button>
                </div>
              </div>

              {/* Plan cards */}
              <div className={`grid gap-4 mb-4 ${plans.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'}`}>
                {plans.map(plan => {
                  const monthlyPrice = billingInterval === 'annual'
                    ? Math.round(plan.amount_annual_pence / 12)
                    : plan.amount_monthly_pence

                  return (
                    <div
                      key={plan.id}
                      className="p-4 rounded-2xl border-2 text-left relative flex flex-col transition border-[var(--border-color)] hover:border-[var(--color-accent)]/40"
                      style={{ backgroundColor: 'var(--bg-card)' }}
                    >
                      <h4 className="text-base font-bold text-[var(--text-primary)]">{plan.name}</h4>
                      <p className="text-xs text-[var(--text-muted)] mb-3">
                        Up to {plan.max_properties} properties
                      </p>

                      <div className="mb-3">
                        <span className="text-2xl font-bold text-[var(--text-primary)]">
                          {formatPrice(monthlyPrice)}
                        </span>
                        <span className="text-sm text-[var(--text-muted)] ml-1">
                          /month
                        </span>
                        {billingInterval === 'annual' && (
                          <p className="text-xs font-semibold mt-0.5" style={{ color: '#16a34a' }}>
                            {formatPrice(plan.amount_annual_pence)}/yr — save {calculateAnnualSavings(plan.amount_monthly_pence, plan.amount_annual_pence)}%
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-7 h-7 rounded-lg bg-[var(--color-accent)]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[var(--color-accent)]">{plan.max_properties}</span>
                        </div>
                        <span className="text-xs font-medium text-[var(--text-secondary)]">
                          Up to {plan.max_properties} properties
                        </span>
                      </div>

                      {!!plan.features?.priority_support && (
                        <div className="flex items-center gap-2 mb-1.5">
                          <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 ml-1.5" />
                          <span className="text-xs font-medium text-[var(--text-secondary)]">Priority support</span>
                        </div>
                      )}

                      <div className="flex-1" />

                      <button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={actionLoading}
                        data-readonly-safe="true"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-accent)] bg-[var(--color-accent)]/10 hover:bg-[var(--color-accent)]/20"
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : !subscriptionStatus ? (
                          'Start 14-Day Free Trial'
                        ) : (
                          'Subscribe'
                        )}
                      </button>
                      {!subscriptionStatus && (
                        <button
                          onClick={() => handleSubscribe(plan.id, 0)}
                          disabled={actionLoading}
                          data-readonly-safe="true"
                          className="w-full text-xs mt-2 py-1 cursor-pointer transition disabled:opacity-50 hover:underline"
                          style={{ color: 'var(--text-secondary)', background: 'none', border: 'none' }}
                        >
                          Pay now - skip trial
                        </button>
                      )}
                    </div>
                  )
                })}

                {/* Agency Tier */}
                <div
                  className="p-4 rounded-2xl border-2 text-left relative flex flex-col"
                  style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                >
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap" style={{ backgroundColor: '#1a2744', color: 'white' }}>
                    Enterprise
                  </span>
                  <h4 className="text-base font-bold text-[var(--text-primary)]">Agency</h4>
                  <p className="text-xs text-[var(--text-muted)] mb-3">25+ properties</p>

                  <div className="mb-3">
                    <span className="text-2xl font-bold text-[var(--text-primary)]">Custom</span>
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">Tailored to your portfolio</p>
                  </div>

                  <ul className="space-y-1.5 mb-4 flex-1">
                    {[
                      'Everything in Portfolio',
                      'Unlimited properties',
                      'Dedicated support',
                    ].map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="mailto:support@lentals.com?subject=Agency Plan Enquiry"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition"
                    style={{ backgroundColor: '#1a2744', color: 'white' }}
                  >
                    Contact Us
                  </a>
                </div>
              </div>

              {/* All plans include */}
              <div className="mb-4 px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-color)]">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">All plans include</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-1.5">
                  {[
                    'Rent tracking & financials',
                    'Compliance reminders & document storage',
                    'Unlimited issues',
                    'QR/NFC/link-based reporting',
                    'Contractor portal',
                    'Tenant Hub access',
                    'Email & push notifications',
                    'Full audit trail',
                    'Mobile-friendly',
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                      <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {error && <p className="text-xs mb-2" style={{ color: 'var(--color-error, #ef4444)' }}>{error}</p>}

              <p className="text-xs text-[var(--text-muted)] text-center">
                {!subscriptionStatus
                  ? 'Card required. Cancel anytime during trial.'
                  : 'Cancel anytime. No long-term commitment.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
